import React, { useState } from 'react';
import { Box, Typography, IconButton, Snackbar } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { HttpMethod, getHttpMethodColor } from '../utils/httpMethodColors';

interface ApiEndpointDisplayProps {
    method: HttpMethod;
    url: string;
}

const ApiEndpointDisplay: React.FC<ApiEndpointDisplayProps> = ({ method, url }) => {
    const [showCopySuccess, setShowCopySuccess] = useState(false);
    
    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setShowCopySuccess(true);
        } catch (error) {
            const textArea = document.createElement('textarea');
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setShowCopySuccess(true);
        }
    };
    
    const handleCloseCopySuccess = () => {
        setShowCopySuccess(false);
    };
    
    return (
        <>
            <Box 
                sx={{ 
                    backgroundColor: '#333a45ff',
                    padding: 2,
                    borderRadius: 2,
                    marginBottom: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    border: '1px solid #4a5568',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                }}
            >
                <Typography 
                    variant="body2"
                    fontSize={12}
                    fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                    fontWeight={700}
                    sx={{
                        color: 'white',
                        backgroundColor: getHttpMethodColor(method),
                        padding: '4px 8px',
                        borderRadius: '6px',
                        border: `2px solid ${getHttpMethodColor(method)}`,
                        minWidth: '40px',
                        textAlign: 'center',
                        letterSpacing: '0.5px'
                    }}
                >
                    {method}
                </Typography>
                
                <Box 
                    sx={{
                        width: '1px',
                        height: '20px',
                        backgroundColor: '#565b62ff',
                        flexShrink: 0
                    }}
                />                
                <Typography 
                    variant="body2"
                    fontSize={14}
                    fontFamily="Monaco, Consolas, 'Courier New', monospace"
                    fontWeight={500}
                    sx={{
                        flex: 1,
                        wordBreak: 'break-all',
                        color: '#e2e8f0',
                        cursor: 'text',
                        userSelect: 'text',
                        lineHeight: 1.5
                    }}
                >
                    {url}
                </Typography>
                
                <IconButton
                    size="small"
                    onClick={handleCopyUrl}
                    sx={{
                        padding: '6px',
                        color: '#a0aec0',
                        '&:hover': {
                            backgroundColor: '#4a5568',
                            color: '#e2e8f0'
                        }
                    }}
                    title="Copy URL"
                >
                    <ContentCopyIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
            
            <Snackbar
                open={showCopySuccess}
                autoHideDuration={2000}
                onClose={handleCloseCopySuccess}
                message="URL copied to clipboard!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </>
    );
};

export default ApiEndpointDisplay;
